import { Platform } from 'react-native';
import { HOST } from '@api/constant';
import { store } from '@reduxLocal/persistState';
import WatchSessionBridge from 'watch-session-bridge';

export type SyncWatchSessionOptions = {
  /** Override company id (e.g. right after SWITCH_COMPANY_REQUEST) */
  companyId?: string | null;
  companyName?: string | null;
  userId?: string | null;
  targetUserId?: string | null;
  apiBaseUrl?: string | null;
  hermesConnected?: boolean | null;
  openclawConnected?: boolean | null;
};

const COOKIE_RETRY_DELAYS_MS = [0, 400, 1200, 2500];

/** Bumped on clear so in-flight sync retries cannot resurrect a Watch session after logout. */
let watchSessionEpoch = 0;

function isAuthenticated(): boolean {
  return store.getState()?.promiseData?.isAuthenticated === true;
}
function isIntegrationConnected(auth: unknown): boolean {
  if (!auth || typeof auth !== 'object') return false;
  const a = auth as Record<string, unknown>;
  if (a.authorised || a.authorized || a.bridgeTokenHash || a.baseUrl || a.url) return true;
  return Object.keys(a).length > 0;
}

function resolveSessionContext(options: SyncWatchSessionOptions = {}) {
  const state = store.getState();
  const userData = state?.promiseData?.data?.userData;
  const selectedProfile = state?.app?.selectedProfile;
  const sessionCompany =
    state?.promiseData?.data?.company ||
    state?.promiseData?.data?.userData?.currentCompany;
  const authentications =
    userData?.authentications ||
    state?.promiseData?.data?.authentications ||
    {};

  const userId =
    options.userId ??
    userData?.id ??
    userData?._id ??
    null;

  const companyId =
    options.companyId ??
    selectedProfile?.id ??
    sessionCompany?._id ??
    sessionCompany?.id ??
    null;

  const companyName =
    options.companyName ??
    selectedProfile?.companyName ??
    sessionCompany?.company_name ??
    sessionCompany?.copmany_name ??
    null;

  const targetUserId =
    options.targetUserId ??
    state?.promiseData?.data?.targetUser?._id ??
    state?.promiseData?.data?.targetUser?.id ??
    null;

  const apiBaseUrl = (options.apiBaseUrl || (HOST ? `${HOST.replace(/\/+$/, '')}/api` : '')).replace(
    /\/+$/,
    '',
  );

  const hermesConnected =
    options.hermesConnected ?? isIntegrationConnected(authentications?.hermes);
  const openclawConnected =
    options.openclawConnected ?? isIntegrationConnected(authentications?.openclaw);

  return {
    apiBaseUrl,
    userId: userId != null ? String(userId) : null,
    companyId: companyId != null ? String(companyId) : null,
    companyName: companyName != null ? String(companyName) : null,
    targetUserId: targetUserId != null ? String(targetUserId) : null,
    hermesConnected: !!hermesConnected,
    openclawConnected: !!openclawConnected,
  };
}

function sleep(ms: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, ms));
}

function isCookieMissingError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : String((error as { message?: string })?.message ?? error ?? '');
  return /cookie|smartsaas\.sid|sessionCookieNotFound/i.test(message);
}

/** Activate WCSession early so Watch requestWatchSession can be answered. */
export async function activateWatchSessionBridge() {
  if (Platform.OS !== 'ios') return;
  if (!WatchSessionBridge.isAvailable()) return;
  await WatchSessionBridge.activate();
}

/**
 * Push current iOS login (cookie + company) into the App Group for Apple Watch.
 * No-op on web / Android / when native module is missing.
 * Retries briefly — Set-Cookie often lands a beat after OAuth SUCCESS.
 */
export async function syncWatchSession(options: SyncWatchSessionOptions = {}) {
  if (Platform.OS !== 'ios') return { ok: false as const, reason: 'not_ios' as const };
  if (!WatchSessionBridge.isAvailable()) {
    if (__DEV__) {
      console.warn(
        '[watchSession] Native module unavailable — rebuild with `npx expo run:ios` after adding watch-session-bridge',
      );
    }
    return { ok: false as const, reason: 'module_unavailable' as const };
  }

  if (!isAuthenticated()) {
    return { ok: false as const, reason: 'not_authenticated' as const };
  }

  const epoch = watchSessionEpoch;
  await WatchSessionBridge.activate();
  if (epoch !== watchSessionEpoch || !isAuthenticated()) {
    return { ok: false as const, reason: 'cancelled' as const };
  }

  const context = resolveSessionContext(options);
  if (!context.apiBaseUrl) {
    return { ok: false as const, reason: 'missing_api_base' as const };
  }

  let lastError: unknown;
  for (let i = 0; i < COOKIE_RETRY_DELAYS_MS.length; i++) {
    if (epoch !== watchSessionEpoch || !isAuthenticated()) {
      return { ok: false as const, reason: 'cancelled' as const };
    }
    const delay = COOKIE_RETRY_DELAYS_MS[i];
    if (delay > 0) await sleep(delay);
    if (epoch !== watchSessionEpoch || !isAuthenticated()) {
      return { ok: false as const, reason: 'cancelled' as const };
    }
    try {
      const result = await WatchSessionBridge.syncSession(context);
      if (epoch !== watchSessionEpoch || !isAuthenticated()) {
        // Logout won the race — push clear again so Watch does not keep the late sync.
        void WatchSessionBridge.clearSession();
        return { ok: false as const, reason: 'cancelled' as const };
      }
      if (__DEV__) {
        console.log('[watchSession] synced', {
          hasCompany: result.hasCompany,
          updatedAt: result.updatedAt,
          hermesConnected: context.hermesConnected,
          openclawConnected: context.openclawConnected,
          attempt: i + 1,
        });
      }
      return { ok: true as const, result };
    } catch (error) {
      lastError = error;
      if (!isCookieMissingError(error) || i === COOKIE_RETRY_DELAYS_MS.length - 1) {
        break;
      }
    }
  }

  if (__DEV__) {
    console.warn('[watchSession] sync failed', lastError);
  }
  return { ok: false as const, reason: 'sync_failed' as const, error: lastError };
}

/** Clear Watch-visible session (call on logout / session expiry). */
export async function clearWatchSession() {
  if (Platform.OS !== 'ios') return;
  // Invalidate any in-flight syncWatchSession retries immediately (even if module missing).
  watchSessionEpoch += 1;
  if (!WatchSessionBridge.isAvailable()) return;
  try {
    await WatchSessionBridge.activate();
    await WatchSessionBridge.clearSession();
    if (__DEV__) console.log('[watchSession] cleared');
  } catch (error) {
    if (__DEV__) console.warn('[watchSession] clear failed', error);
  }
}

/** Fire-and-forget helpers for Redux/auth paths (never throw into UI). */
export function syncWatchSessionLater(options?: SyncWatchSessionOptions) {
  void syncWatchSession(options);
}

export function clearWatchSessionLater() {
  void clearWatchSession();
}

export function activateWatchSessionBridgeLater() {
  void activateWatchSessionBridge();
}
